import {
  Card,
  CardContent,
} from "@lazarentertainment/web-design-system/components/card"
import { EmptyState } from "@lazarentertainment/web-design-system/components/emptyState"
import { CalendarSolid } from "@lazarentertainment/web-design-system/components/iconSolid"
import { useChannelContext } from "client/components/channel"
import { UserAvatarImage } from "client/components/channel/UserAvatarImage"
import { ScheduledShow } from "client/graphql"

import { FeaturedUpcomingShowCard } from "../FeaturedUpcomingShowCard"
import { getUpcomingShows } from "../UpcomingShowsList"

import styles from "./OfflineCard.module.css"

export const DEFAULT_SPLASH_IMAGE_URL = "/static/default-splash.png"

export type OfflineCardProps = {
  /**
   * When true, the FeaturedUpcomingShowCard will use `object-fit: contain` when in mobile mode (<800px width).
   * This is needed for the /embed page, where we need the card to fit within ANY size of embed,
   * and we don't have the luxury of allowing the page to scroll vertically.
   *
   * Without compact mode, the FeaturedUpcomingShowCard would be too tall for mobile mode in lots of cases.
   *
   * @default false
   */
  compactMode?: boolean
}

/**
 * When a channel isn't live, this component displays the content which goes in place of the Live Player.
 * - If there's an upcoming show, displays the upcoming show card
 * - If there isn't an upcoming show, displays an empty state ('You have no upcoming shows')
 */
export default function OfflineCard({
  compactMode = false,
}: OfflineCardProps) {
  const { title, scheduledShows } = useChannelContext()

  const upcomingShows = getUpcomingShows(scheduledShows)
  const closestUpcomingShow: ScheduledShow | undefined = upcomingShows[0]

  if (closestUpcomingShow) {
    const countDownDateMs = new Date(
      closestUpcomingShow.startTime,
    ).getTime()
    return (
      <FeaturedUpcomingShowCard
        title={closestUpcomingShow.title}
        description={closestUpcomingShow.description}
        channelTitle={title}
        avatarImage={<UserAvatarImage size="xs" />}
        splashImage={
          closestUpcomingShow.splashImageUrl ? (
            <img
              src={closestUpcomingShow.splashImageUrl}
              alt={closestUpcomingShow.title}
              draggable={false}
            />
          ) : undefined
        }
        countdownDateMs={countDownDateMs}
        compactMode={compactMode}
      />
    )
  }
  return (
    <Card className={styles.emptyStateCard}>
      <CardContent className={styles.emptyStateCardContent}>
        <EmptyState
          icon={CalendarSolid}
          title="No upcoming shows yet"
          description="Looks like this creator hasn't scheduled any future shows. Check back later for more!"
        />
      </CardContent>
    </Card>
  )
}
